import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, Phone, MessageCircle, HelpCircle } from "lucide-react";
import axios from "axios";

const LOGO_URL = "/logo-marketvalley.png";

const LoginPage = () => {
  const { login, loginWithGoogle, setToken, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check if coming from "Registro" button (tab=whatsapp)
  const initialTab = searchParams.get('tab') === 'whatsapp' ? 'whatsapp' : 'email';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Email login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // WhatsApp login state
  const [waUsername, setWaUsername] = useState("");
  const [waPassword, setWaPassword] = useState("");
  const [waLoading, setWaLoading] = useState(false);
  
  // Help dialogs state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotUsernameOpen, setForgotUsernameOpen] = useState(false);
  const [changeWhatsappOpen, setChangeWhatsappOpen] = useState(false);
  const [recoveryWhatsapp, setRecoveryWhatsapp] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  
  // Change WhatsApp state
  const [changeUsername, setChangeUsername] = useState("");
  const [changeOldWa, setChangeOldWa] = useState("");
  const [changeNewWa, setChangeNewWa] = useState("");
  const [changeLoading, setChangeLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  // Email login handler
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success("¡Bienvenido de vuelta!");
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp login handler
  const handleWhatsAppLogin = async (e) => {
    e.preventDefault();
    if (!waUsername || !waPassword) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    if (waPassword.length !== 8) {
      toast.error("La contraseña debe ser de 8 dígitos");
      return;
    }

    setWaLoading(true);
    try {
      const payload = {
        username: waUsername.trim().toLowerCase(),
        whatsapp: waPassword.trim()
      };
      console.log("Login payload:", payload);
      
      const response = await axios.post(`${API}/auth/whatsapp-auth`, payload);
      
      if (!response.data.token) {
        toast.error("Respuesta inválida del servidor");
        return;
      }
      
      // Set auth state
      setToken(response.data.token);
      setUser(response.data.user);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      if (response.data.is_new) {
        toast.success("¡Cuenta creada! Bienvenido a Clueer 🎉");
      } else {
        toast.success("¡Bienvenido de vuelta!");
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Login error full:", error);
      console.error("Login error response:", error.response);
      console.error("Login error data:", error.response?.data);
      
      // More specific error handling
      if (error.response) {
        // Server responded with error
        const message = error.response.data?.detail || `Error ${error.response.status}`;
        toast.error(message);
      } else if (error.request) {
        // Request made but no response
        toast.error("No se pudo conectar al servidor");
      } else {
        // Error setting up request
        toast.error("Error de conexión: " + error.message);
      }
    } finally {
      setWaLoading(false);
    }
  };

  // Recover username handler
  const handleRecoverUsername = async () => {
    if (recoveryWhatsapp.length !== 8) {
      toast.error("Ingresa los 8 dígitos de tu WhatsApp");
      return;
    }

    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/recover-username`, {
        whatsapp: recoveryWhatsapp
      });
      
      toast.success("Si el número está registrado, recibirás tu usuario por WhatsApp");
      setForgotUsernameOpen(false);
      setRecoveryWhatsapp("");
    } catch (error) {
      toast.error("Error al procesar la solicitud");
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Change WhatsApp handler
  const handleChangeWhatsapp = async () => {
    if (!changeUsername || changeOldWa.length !== 8 || changeNewWa.length !== 8) {
      toast.error("Completa todos los campos correctamente");
      return;
    }

    if (!changeNewWa.startsWith('6')) {
      toast.error("El nuevo número debe iniciar con 6");
      return;
    }

    setChangeLoading(true);
    try {
      await axios.put(`${API}/auth/update-whatsapp`, {
        username: changeUsername,
        old_whatsapp: changeOldWa,
        new_whatsapp: changeNewWa
      });
      
      toast.success("WhatsApp actualizado exitosamente");
      setChangeWhatsappOpen(false);
      setChangeUsername("");
      setChangeOldWa("");
      setChangeNewWa("");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar WhatsApp";
      toast.error(message);
    } finally {
      setChangeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#E2E8F0] flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Logo - Clickable to go home */}
        <Link to="/" className="block text-center mb-6">
          <img src={LOGO_URL} alt="Clueer" className="h-16 w-auto mx-auto mb-2 hover:opacity-80 transition-opacity cursor-pointer" />
          <p className="text-[#64748B]">Marketplace de Subastas</p>
        </Link>

        {/* Mobile Tab Selector */}
        <div className="flex md:hidden bg-white rounded-full p-1 shadow-sm mb-4 mx-auto max-w-xs">
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
              activeTab === 'whatsapp' 
                ? 'bg-[#34D399] text-white' 
                : 'text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            WhatsApp
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
              activeTab === 'email' 
                ? 'bg-[#475569] text-white' 
                : 'text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            Correo
          </button>
        </div>

        {/* Login Panels Container */}
        <div className="grid md:grid-cols-2 gap-6">
          
          {/* WhatsApp Login Panel - First on mobile when tab=whatsapp */}
          <div 
            className={`bg-white rounded-2xl shadow-lg p-6 border-2 border-[#34D399] ${
              activeTab === 'whatsapp' ? 'block' : 'hidden md:block'
            } ${activeTab === 'whatsapp' ? 'md:order-1' : 'md:order-2'}`}
            data-testid="whatsapp-login-panel"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <MessageCircle className="text-[#34D399]" size={24} />
                <h2 className="text-xl font-bold text-[#0F172A] whitespace-nowrap">
                  {activeTab === 'whatsapp' ? 'Acceso Rápido/Registro' : 'Con WhatsApp'}
                </h2>
              </div>
              <span className="inline-block text-xs bg-[#34D399]/10 text-[#34D399] px-2 py-0.5 rounded-full mt-1.5 ml-6">Recomendado</span>
            </div>

            <form onSubmit={handleWhatsAppLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wa-username">Usuario</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={18} />
                  <Input
                    id="wa-username"
                    type="text"
                    placeholder="tu_usuario"
                    value={waUsername}
                    onChange={(e) => setWaUsername(e.target.value.toLowerCase())}
                    className="pl-10 rounded-xl border-[#E2E8F0]"
                    data-testid="wa-username-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wa-password">WhatsApp (tu contraseña)</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0 bg-[#34D399] text-white px-3 py-2 rounded-l-xl text-sm font-medium">
                    +507
                  </div>
                  <Input
                    id="wa-password"
                    type="tel"
                    placeholder="Ej: 61234567"
                    value={waPassword}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                      setWaPassword(value);
                    }}
                    className="rounded-l-none rounded-r-xl border-[#E2E8F0]"
                    maxLength={8}
                    data-testid="wa-password-input"
                  />
                </div>
                <p className="text-xs text-[#94A3B8]">Los 8 dígitos serán tu contraseña. <span className="text-[#10B981] font-semibold">La primera vez, tu cuenta se registra automáticamente.</span></p>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full bg-[#34D399] hover:bg-[#10B981] h-11 text-white"
                disabled={waLoading || waUsername.length < 3 || waPassword.length !== 8}
                data-testid="wa-login-btn"
              >
                {waLoading ? "Ingresando..." : "Ingresar / Registrarse"}
              </Button>
            </form>

            <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
              <p className="text-xs text-[#64748B] mb-2">¿Necesitas ayuda?</p>
              <div className="flex flex-wrap gap-2">
                <button 
                  onClick={() => setForgotUsernameOpen(true)}
                  className="text-xs text-[#34D399] hover:underline flex items-center gap-1"
                >
                  <HelpCircle size={12} />
                  Olvidé mi usuario
                </button>
                <button 
                  onClick={() => setChangeWhatsappOpen(true)}
                  className="text-xs text-[#34D399] hover:underline flex items-center gap-1"
                >
                  <Phone size={12} />
                  Cambiar WhatsApp
                </button>
              </div>
            </div>

            {/* Mobile: Link to email option */}
            <div className="md:hidden mt-4 pt-4 border-t border-[#E2E8F0] text-center">
              <button 
                onClick={() => setActiveTab('email')}
                className="text-sm text-[#64748B] hover:text-[#0F172A]"
              >
                ¿Prefieres usar <span className="font-medium">Correo o Google</span>?
              </button>
            </div>
          </div>

          {/* Email Login Panel */}
          <div 
            className={`bg-white rounded-2xl shadow-lg p-6 border border-[#E2E8F0] ${
              activeTab === 'email' ? 'block' : 'hidden md:block'
            } ${activeTab === 'whatsapp' ? 'md:order-2' : 'md:order-1'}`}
            data-testid="email-login-panel"
          >
            <div className="flex items-center gap-2 mb-6">
              <Mail className="text-[#475569]" size={24} />
              <h2 className="text-xl font-bold text-[#0F172A]">Con Email</h2>
            </div>

            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl border-[#E2E8F0]"
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10 rounded-xl border-[#E2E8F0]"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full bg-[#475569] hover:bg-[#334155] h-11"
                disabled={loading}
                data-testid="email-login-submit"
              >
                {loading ? <div className="spinner" style={{ width: 20, height: 20 }} /> : "Entrar"}
              </Button>
            </form>

            <div className="my-4">
              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-[#94A3B8]">
                  o
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full rounded-full h-11 border-[#E2E8F0]"
              onClick={loginWithGoogle}
              data-testid="google-login"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </Button>

            <p className="text-center text-xs text-[#94A3B8] mt-4">
              <Link to="/forgot-password" className="hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </p>

            {/* Mobile: Link to WhatsApp option */}
            <div className="md:hidden mt-4 pt-4 border-t border-[#E2E8F0] text-center">
              <button 
                onClick={() => setActiveTab('whatsapp')}
                className="text-sm text-[#64748B] hover:text-[#34D399]"
              >
                ¿Prefieres <span className="font-medium text-[#34D399]">WhatsApp</span>? Es más rápido
              </button>
            </div>
          </div>
        </div>

        {/* Info Text */}
        <p className="text-center text-sm text-[#64748B] mt-6">
          ¿Primera vez? No necesitas registrarte: tu cuenta se crea automáticamente al ingresar con WhatsApp.
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Olvidaste tu contraseña?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4">
              <p className="text-sm text-[#166534]">
                Tu contraseña son los <strong>8 dígitos</strong> de tu número de WhatsApp registrado.
              </p>
              <p className="text-sm text-[#166534] mt-2">
                <strong>Ejemplo:</strong> Si tu WhatsApp es 6123-4567, tu contraseña es: <code className="bg-white px-2 py-1 rounded">61234567</code>
              </p>
            </div>
          </div>
          <Button onClick={() => setForgotPasswordOpen(false)} className="w-full bg-[#34D399] hover:bg-[#10B981]">
            Entendido
          </Button>
        </DialogContent>
      </Dialog>

      {/* Forgot Username Dialog */}
      <Dialog open={forgotUsernameOpen} onOpenChange={setForgotUsernameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Olvidaste tu usuario?</DialogTitle>
            <DialogDescription>
              Ingresa tu WhatsApp y te enviaremos tu usuario por mensaje.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>WhatsApp</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-shrink-0 bg-[#34D399] text-white px-3 py-2 rounded-l-xl text-sm font-medium">
                  +507
                </div>
                <Input
                  type="tel"
                  placeholder="Ej: 61234567"
                  value={recoveryWhatsapp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setRecoveryWhatsapp(value);
                  }}
                  className="rounded-l-none rounded-r-xl"
                  maxLength={8}
                />
              </div>
            </div>
          </div>
          <Button 
            onClick={handleRecoverUsername} 
            disabled={recoveryLoading || recoveryWhatsapp.length !== 8}
            className="w-full bg-[#34D399] hover:bg-[#10B981]"
          >
            {recoveryLoading ? "Enviando..." : "Enviar usuario"}
          </Button>
          <p className="text-xs text-center text-[#94A3B8] mt-2">
            Se enviará a este número si está registrado en nuestra plataforma.
          </p>
        </DialogContent>
      </Dialog>

      {/* Change WhatsApp Dialog */}
      <Dialog open={changeWhatsappOpen} onOpenChange={setChangeWhatsappOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Actualizar WhatsApp</DialogTitle>
            <DialogDescription>
              Ingresa tus datos para cambiar tu número de WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Usuario</Label>
              <Input
                type="text"
                placeholder="tu_usuario"
                value={changeUsername}
                onChange={(e) => setChangeUsername(e.target.value.toLowerCase())}
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label>WhatsApp anterior</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-shrink-0 bg-[#64748B] text-white px-3 py-2 rounded-l-xl text-sm font-medium">
                  +507
                </div>
                <Input
                  type="tel"
                  placeholder="Número anterior"
                  value={changeOldWa}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setChangeOldWa(value);
                  }}
                  className="rounded-l-none rounded-r-xl"
                  maxLength={8}
                />
              </div>
            </div>
            <div>
              <Label>WhatsApp nuevo</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-shrink-0 bg-[#34D399] text-white px-3 py-2 rounded-l-xl text-sm font-medium">
                  +507
                </div>
                <Input
                  type="tel"
                  placeholder="Número nuevo"
                  value={changeNewWa}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setChangeNewWa(value);
                  }}
                  className="rounded-l-none rounded-r-xl"
                  maxLength={8}
                />
              </div>
            </div>
          </div>
          <Button 
            onClick={handleChangeWhatsapp} 
            disabled={changeLoading || !changeUsername || changeOldWa.length !== 8 || changeNewWa.length !== 8}
            className="w-full bg-[#34D399] hover:bg-[#10B981]"
          >
            {changeLoading ? "Actualizando..." : "Actualizar WhatsApp"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
